import classNames from "classnames";
import { useCallback, useState } from "react";
import { defineMessages } from "react-intl";
import { components } from "react-select";

import Button from "../Buttons/Button";
import { useFieldContext } from "../Form/Field";
import { useFormatMessage } from "../Intl/Context";
import { useDeferredEffect } from "../Utils/useDeferredEffect";

// This overrides the Menu component from react-select to add the `role="listbox"` prop to
// the menu's wrapping div, to ensure that screen readers correctly voice that the menu is
// going to behave as the listbox of a combobox.
//
const CustomMenu = ({ innerProps: originalInnerProps, ...originalProps }) => {
  const innerProps = { ...originalInnerProps, role: "listbox" };
  const props = { ...originalProps, innerProps };

  return <components.Menu {...props} />;
};

// This enhances the react-select's Option component to add the required role="option"
// and aria-selected attributes to ensure that screen readers correctly voice options as
// such, and when they're selected.
//
const CustomOption = ({
  innerProps: originalInnerProps,
  isSelected,
  ...originalProps
}) => {
  const innerProps = {
    ...originalInnerProps,
    role: "option",
    "aria-selected": isSelected
  };
  const props = { ...originalProps, isSelected, innerProps };

  return <components.Option {...props} />;
};

// This enhances the react-select's Input component to add the required aria-haspopup
// "listbox" value (it's "true" by default), and the "aria-activedescendant" attribute
// which allows screen reader to voice to their users which option is currently focused
// within the dropdown menu.
//
const CustomInput = ({ selectProps, ...originalProps }) => {
  const { select } = selectProps;
  const [focusedOptionId, setFocusedOptionId] = useState();

  // The deferred effect here is needed to ensure that the focusedOptionref is fetched
  // after the React rendering phase has happened. This is required since without the
  // deferring, we get the previously focused option when calling
  // `select.focusedOptionRef`.
  //
  // Since we want to run the deferred effect on each render of the input, as we don't
  // have any accessible value to depend on, we just silence eslint warning below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useDeferredEffect(() => {
    const newFocusedOptionId = select?.focusedOptionRef?.id;

    if (newFocusedOptionId !== focusedOptionId) setFocusedOptionId(newFocusedOptionId);
  });

  const ariaDescribedBy = selectProps["aria-describedby"];
  const title = selectProps["placeholder"];
  const { isRequired, isInvalid } = useFieldContext();

  // Note : For some reason, the props API for the Input component differs from the other
  // components' props API, as it doesn't provide nor use an `innerProps` prop which would
  // have contained the props that are meant to be spread on the actual HTML tag (here :
  // the <input> tag).
  const props = {
    ...originalProps,
    selectProps,
    title,
    "aria-haspopup": "listbox",
    "aria-activedescendant": focusedOptionId,
    "aria-describedby": ariaDescribedBy,
    "aria-required": isRequired,
    "aria-invalid": isInvalid,
    autoCorrect: null
  };

  return <components.Input {...props} />;
};

const CustomClearIndicator = ({
  selectProps: { select, clearIndicatorAriaLabel },
  innerProps,
  clearValue,
  innerRef,
  ...props
}) => {
  const handleClick = useCallback(() => {
    clearValue();
    select.focus();
  }, [clearValue, select]);

  // We need to stop propagation for the `SPACE` key as otherwise, space on this control
  // would open the options dropdown
  const handleKeyDown = useCallback(e => e.stopPropagation(), []);

  const className = classNames("btn-unstyled", "padding-xxs", props.className);

  return (
    <Button
      {...innerProps}
      type="button"
      ref={innerRef}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={className}
      aria-hidden={null}
      aria-label={clearIndicatorAriaLabel}
      // With tabindex="-1", visual items that control the state of the combobox are
      // accessible with mouse and touch controls, by screen readers, but are not
      // tabbable, which avoids keyboard users to focus them. This is a MDN ARIA
      // documentation recommentation that was approved by the RGAA auditor
      tabIndex="-1"
    >
      <components.CrossIcon />
    </Button>
  );
};

const CustomDropdownIndicator = ({
  selectProps: { dropdownIndicatorAriaLabel, select },
  innerProps: { onMouseDown, ...innerProps },
  innerRef,
  ...props
}) => {
  const className = classNames("btn-unstyled", "padding-xxs", props.className);

  const [isExpanded, setisExpanded] = useState(false);

  // On each new render of the component, we retrigger this deferred effect which waits
  // for the rendering stack to be finished, and analyzes the select input to copy its
  // aria-expanded value.
  //
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useDeferredEffect(() => {
    const newIsExpanded = select?.inputRef?.getAttribute("aria-expanded") || false;

    if (newIsExpanded !== isExpanded) setisExpanded(newIsExpanded);
  });

  return (
    <Button
      {...innerProps}
      type="button"
      ref={innerRef}
      onMouseDown={onMouseDown}
      className={className}
      aria-hidden={null}
      aria-label={dropdownIndicatorAriaLabel}
      aria-expanded={isExpanded}
      // With tabindex="-1", visual items that control the state of the combobox are
      // accessible with mouse and touch controls, by screen readers, but are not
      // tabbable, which avoids keyboard users to focus them. This is a MDN ARIA
      // documentation recommentation that was approved by the RGAA auditor
      tabIndex="-1"
    >
      <components.DownChevron />
    </Button>
  );
};

// This allows us to override the default `ariaLiveMessages` prop of the `react-select`
// lib. Since there's no dedicated method to only override the text messages, the whole
// logic was copied from the original lib source files, and the text messages were adapted
// to be translatable.
//
const useAriaLiveMessages = () => {
  const t = useFormatMessage();

  return {
    guidance: props => {
      const { isSearchable, isMulti, isDisabled, tabSelectsValue, context } = props;

      const ariaLiveGuidanceMenuBase = t(intlMessages.ariaLiveGuidanceMenuBase);
      const ariaLiveGuidanceMenuEnter = t(intlMessages.ariaLiveGuidanceMenuEnter);
      const ariaLiveGuidanceMenuEscape = t(intlMessages.ariaLiveGuidanceMenuEscape);
      const ariaLiveGuidanceMenuTab = t(intlMessages.ariaLiveGuidanceMenuTab);
      const ariaLiveGuidanceInputSelect = t(intlMessages.ariaLiveGuidanceInputSelect);
      const ariaLiveGuidanceInputIsFocused = t(
        intlMessages.ariaLiveGuidanceInputIsFocused
      );
      const ariaLiveGuidanceInputSearchable = t(
        intlMessages.ariaLiveGuidanceInputSearchable
      );
      const ariaLiveGuidanceInputDown = t(intlMessages.ariaLiveGuidanceInputDown);
      const ariaLiveGuidanceInputLeft = t(intlMessages.ariaLiveGuidanceInputLeft);
      const ariaLiveGuidanceValue = t(intlMessages.ariaLiveGuidanceValue);

      switch (context) {
        case "menu":
          return `${ariaLiveGuidanceMenuBase}${
            isDisabled ? "" : ariaLiveGuidanceMenuEnter
          }${ariaLiveGuidanceMenuEscape}${
            tabSelectsValue ? ariaLiveGuidanceMenuTab : ""
          }.`;
        case "input":
          return `${
            props["aria-label"] || ariaLiveGuidanceInputSelect
          } ${ariaLiveGuidanceInputIsFocused}${
            isSearchable ? ariaLiveGuidanceInputSearchable : ""
          }${ariaLiveGuidanceInputDown}${isMulti ? ` ${ariaLiveGuidanceInputLeft}` : ""}`;
        case "value":
          return ariaLiveGuidanceValue;
        default:
          return "";
      }
    },
    onChange: props => {
      const { action, label = "", labels, isDisabled } = props;
      const ariaLiveOnChangeClear = t(intlMessages.ariaLiveOnChangeClear);

      switch (action) {
        case "deselect-option":
        case "pop-value":
        case "remove-value":
          return t(intlMessages.ariaLiveOnChangeRemoveValue, { label });
        case "clear":
          return ariaLiveOnChangeClear;
        case "initial-input-focus":
          return t(intlMessages.ariaLiveOnChangeInitialInputFocus, {
            labels: labels.join(", "),
            count: labels.length
          });
        case "select-option":
          return isDisabled
            ? t(intlMessages.ariaLiveOnChangeDisabledOption, { label })
            : t(intlMessages.ariaLiveOnChangeSelectedOption, { label });
        default:
          return "";
      }
    },
    onFocus: props => {
      const {
        context,
        focused,
        options,
        label = "",
        selectValue,
        isDisabled,
        isSelected
      } = props;

      const ariaLiveOnFocusDisabled = t(intlMessages.ariaLiveOnFocusDisabled);
      const ariaLiveOnFocusSelected = t(intlMessages.ariaLiveOnFocusSelected);
      const ariaLiveOnFocusFocused = t(intlMessages.ariaLiveOnFocusFocused);

      const getArrayIndex = (arr, item) =>
        arr && arr.length
          ? t(intlMessages.ariaLiveOnFocusArrayIndex, {
            index: arr.indexOf(item) + 1,
            length: arr.length
          })
          : "";

      if (context === "value" && selectValue) {
        return t(intlMessages.ariaLiveOnFocusValue, {
          label,
          index: getArrayIndex(selectValue, focused)
        });
      }

      if (context === "menu") {
        const disabled = isDisabled ? ariaLiveOnFocusDisabled : "";
        const status = ` ${
          isSelected ? ariaLiveOnFocusSelected : ariaLiveOnFocusFocused
        }${disabled}`;

        return t(intlMessages.ariaLiveOnFocusMenu, {
          label,
          status,
          index: getArrayIndex(options, focused)
        });
      }
      return "";
    },
    onFilter: props => {
      const { inputValue, resultsMessage } = props;
      return `${resultsMessage}${
        inputValue ? " " + t(intlMessages.ariaLiveOnFilterSearch, { inputValue }) : ""
      }.`;
    }
  };
};

export const useReactSelectConfigProps = () => {
  // This allows getting a reference to the ReactSelect root component instance, which is
  // the one that handles most of the coordination for the lib. It's used by our
  // <CustomInput> component to fetch which option is currently focused.
  const [select, setSelect] = useState(null);

  // These are the translations for every text that's used by the lib to display visual
  // feedback or indications to the user.
  const t = useFormatMessage();
  const ariaLabel = t(intlMessages.placeholder);
  const placeholder = t(intlMessages.placeholder);
  const noOptionsMessage = ({ inputValue }) =>
    inputValue
      ? t(intlMessages.noOptions, { terms: inputValue })
      : t(intlMessages.typeSearch);
  const loadingMessage = ({ inputValue }) =>
    t(intlMessages.loading, { terms: inputValue });
  const screenReaderStatus = ({ count }) => t(intlMessages.screenReaderStatus, { count });
  const dropdownIndicatorAriaLabel = t(intlMessages.dropdownIndicatorAriaLabel);
  const clearIndicatorAriaLabel = t(intlMessages.clearIndicatorAriaLabel);
  const ariaLiveMessages = useAriaLiveMessages();

  return {
    ref: setSelect,
    select,
    components: {
      Menu: CustomMenu,
      Option: CustomOption,
      Input: CustomInput,
      ClearIndicator: CustomClearIndicator,
      DropdownIndicator: CustomDropdownIndicator
    },
    "aria-label": ariaLabel,
    placeholder,
    noOptionsMessage,
    loadingMessage,
    screenReaderStatus,
    dropdownIndicatorAriaLabel,
    clearIndicatorAriaLabel,
    ariaLiveMessages
  };
};

const intlMessages = defineMessages({
  placeholder: {
    id: "select.placeholder",
    defaultMessage: "Choisissez une option"
  },
  noOptions: {
    id: "select.noOptions",
    defaultMessage: "Aucun résultat disponible pour la recherche {terms}"
  },
  typeSearch: {
    id: "select.typeSearch",
    defaultMessage: "Tapez une recherche pour voir les options"
  },
  loading: {
    id: "select.loading",
    defaultMessage: "Chargement des résultats pour la recherche {terms}..."
  },
  screenReaderStatus: {
    id: "select.screenReaderStatus",
    defaultMessage: `{count, plural,
      =0 {Aucun résultat disponible}
      one {{count} résultat disponible}
      other {{count} résultats disponibles}
    }`
  },
  dropdownIndicatorAriaLabel: {
    id: "select.dropdownIndicatorAriaLabel",
    defaultMessage: "Afficher les options"
  },
  clearIndicatorAriaLabel: {
    id: "select.clearIndicatorAriaLabel",
    defaultMessage: "Remettre à zéro la sélection"
  },
  ariaLiveGuidanceMenuBase: {
    id: "select.ariaLiveGuidanceMenuBase",
    defaultMessage: `
      Utilisez les touches "flèche haut" et "flèche bas" pour sélectionner des options
    `
  },
  ariaLiveGuidanceMenuEnter: {
    id: "select.ariaLiveGuidanceMenuEnter",
    defaultMessage:
      ", appuyez sur la touche \"Entrée\" pour sélectionner l'option actuellement ciblée"
  },
  ariaLiveGuidanceMenuEscape: {
    id: "select.ariaLiveGuidanceMenuEscape",
    defaultMessage: ", appuyez sur la touche \"Échap\" pour sortir du menu"
  },
  ariaLiveGuidanceMenuTab: {
    id: "select.ariaLiveGuidanceMenuTab",
    defaultMessage:
      ", appuyez sur la touche \"Tab\" pour sélectionner l'option actuellement ciblée et sortir du menu"
  },
  ariaLiveGuidanceInputSelect: {
    id: "select.ariaLiveGuidanceInputSelect",
    defaultMessage: "Le champ de sélection"
  },
  ariaLiveGuidanceInputIsFocused: {
    id: "select.ariaLiveGuidanceInputIsFocused",
    defaultMessage: " est ciblé"
  },
  ariaLiveGuidanceInputSearchable: {
    id: "select.ariaLiveGuidanceInputSearchable",
    defaultMessage: ", écrivez pour affiner la liste"
  },
  ariaLiveGuidanceInputDown: {
    id: "select.ariaLiveGuidanceInputDown",
    defaultMessage: ", appuyez sur la touche \"flèche bas\" pour ouvrir le menu"
  },
  ariaLiveGuidanceInputLeft: {
    id: "select.ariaLiveGuidanceInputLeft",
    defaultMessage:
      ", appuyez sur la touche \"flèche gauche\" pour cibler les valeurs sélectionnées"
  },
  ariaLiveGuidanceValue: {
    id: "select.ariaLiveGuidanceValue",
    defaultMessage: `
      Utilisez les touches "flèche gauche" et "flèche droite" pour vous déplacer entre 
      les valeurs ciblées, appuyez sur la touche "effacer" pour supprimer la valeur
      actuellement ciblée.
    `
  },
  ariaLiveOnChangeRemoveValue: {
    id: "select.ariaLiveOnChangeRemoveValue",
    defaultMessage: " option {label}, déselectionnée."
  },
  ariaLiveOnChangeClear: {
    id: "select.ariaLiveOnChangeClear",
    defaultMessage: "Toutes les options sélectionnées ont été supprimées."
  },
  ariaLiveOnChangeInitialInputFocus: {
    id: "select.ariaLiveOnChangeInitialInputFocus",
    defaultMessage: `
      {count, plural, one { option } other { options }} {labels}, 
      {count, plural, one { sélectionnée } other { sélectionnées }}.
    `
  },
  ariaLiveOnChangeDisabledOption: {
    id: "select.ariaLiveOnChangeDisabledOption",
    defaultMessage: " option {label} est désactivée. Sélectionnez une autre option."
  },
  ariaLiveOnChangeSelectedOption: {
    id: "select.ariaLiveOnChangeSelectedOption",
    defaultMessage: " option {label}, sélectionnée."
  },
  ariaLiveOnFocusArrayIndex: {
    id: "select.ariaLiveOnFocusArrayIndex",
    defaultMessage: " élément {index} sur {length}"
  },
  ariaLiveOnFocusValue: {
    id: "select.ariaLiveOnFocusValue",
    defaultMessage: " la valeur {label} est actuellement ciblée, {index}."
  },
  ariaLiveOnFocusDisabled: {
    id: "select.ariaLiveOnFocusDisabled",
    defaultMessage: ", désactivée"
  },
  ariaLiveOnFocusSelected: {
    id: "select.ariaLiveOnFocusSelected",
    defaultMessage: " sélectionnée"
  },
  ariaLiveOnFocusFocused: {
    id: "select.ariaLiveOnFocusFocused",
    defaultMessage: " ciblée"
  },
  ariaLiveOnFocusMenu: {
    id: "select.ariaLiveOnFocusMenu",
    defaultMessage: " option {label} {status}, {index}."
  },
  ariaLiveOnFilterSearch: {
    id: "select.ariaLiveOnFilterSearch",
    defaultMessage: " pour la recherche : {inputValue}"
  }
});
